import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { MutableRefObject } from 'react';

import { Button } from 'components/Button';
import { Search } from 'components/Search';
import { Skeleton } from 'components/Skeleton';
import { cn, useDebouncedCallback } from 'components/utils';

import {
  filterBarFiltersRecipe,
  filterBarRootRecipe,
  filterBarRowRecipe,
  filterBarSearchRecipe,
  filterBarSummaryRecipe,
} from './FilterBar.recipe';

import type { FilterBarProps, FilterBarSearchProps } from './types';

/**
 * How the search and Clear all reach each other. The search registers what "Clear all" must do to it;
 * the bar calls it. A context and not a ref passed down, because the search is a slot the caller
 * places, not something the bar renders.
 */
type FilterBarContextValue = {
  registerSearch: (handle: { reset: () => void; focus: () => void } | null) => void;
};

const FilterBarContext = createContext<FilterBarContextValue | null>(null);

/** Below this width the bar stacks its parts. The file draws no width between Wide (1160) and Narrow (320). */
const NARROW_BELOW = 576;

/** Widths of the three loading pills, as Figma draws them (96, 140, 88px); none is a spacing step. */
const SKELETON_PILLS = ['w-[96px]', 'w-[140px]', 'w-[88px]'];

/**
 * Design System 2.0 filter bar: the row above a list, card grid or chart that narrows what is shown.
 *
 * Designed in Figma as `Filter bar` (file 5aSkE0U4TeWbuIZDvccHjc, node 2072:145520).
 *
 * ```tsx
 * <FilterBar
 *   hasActiveFilters={active}
 *   clearAllLabel={t('clearAll')}
 *   resultSummary={t('results', { shown, total })}
 *   search={<FilterBarSearch label={t('searchMembers')} clearLabel={t('clearSearch')} value={q} onValueChange={setQ} />}
 *   onClearAll={reset}
 * >
 *   <FilterButton … />
 *   <FilterButton … />
 * </FilterBar>
 * ```
 *
 * It lays the row out and nothing else: it owns no filter state, because every value already lives
 * in the URL or a store. Two slots (`children` for the filters, `search`) rather than a configuration
 * object, so the bar cannot know how many filters a screen has.
 *
 * Clear all cancels a search that is still waiting to be reported, empties the field, calls
 * `onClearAll`, then puts focus on the search or, with none, the first filter. The button unmounts
 * once nothing is active, which would otherwise drop focus on the page.
 */
export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      children,
      search,
      hasActiveFilters,
      onClearAll,
      clearAllLabel,
      resultSummary,
      loading,
      loadingLabel,
      className,
      ...rest
    },
    ref
  ) => {
    const searchHandle = useRef<{ reset: () => void; focus: () => void } | null>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [narrow, setNarrow] = useState(false);

    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref]
    );

    // Zero means "not laid out" (or a test DOM), which is treated as wide rather than as a phone.
    useLayoutEffect(() => {
      const root = rootRef.current;
      if (!root) return undefined;
      const measure = () => {
        const width = root.getBoundingClientRect().width;
        setNarrow(width > 0 && width < NARROW_BELOW);
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(root);
      return () => observer.disconnect();
    }, []);

    const registerSearch = useCallback((handle: { reset: () => void; focus: () => void } | null) => {
      searchHandle.current = handle;
    }, []);

    const handleClearAll = () => {
      searchHandle.current?.reset();
      onClearAll();
      if (searchHandle.current) searchHandle.current.focus();
      else rowRef.current?.querySelector<HTMLElement>('[data-part="filter-button"] button')?.focus();
    };

    return (
      <FilterBarContext.Provider value={{ registerSearch }}>
        <div
          ref={setRootRef}
          className={cn(filterBarRootRecipe(), className)}
          data-layout={narrow ? 'narrow' : 'wide'}
          data-part="filter-bar"
          {...rest}
        >
          <div ref={rowRef} className={filterBarRowRecipe()}>
            {search}

            <div aria-busy={loading || undefined} className={filterBarFiltersRecipe()} data-part="filters">
              {loading ? (
                <>
                  <span className="sr-only" role="status">
                    {loadingLabel}
                  </span>
                  {SKELETON_PILLS.map((width) => (
                    <Skeleton key={width} className={cn('h-10 rounded-full', width)} shape="block" />
                  ))}
                </>
              ) : (
                children
              )}
            </div>

            {hasActiveFilters && !loading && (
              <Button appearance="tertiary" onClick={handleClearAll}>
                {clearAllLabel}
              </Button>
            )}

            <div aria-live="polite" className={filterBarSummaryRecipe()} data-part="summary" role="status">
              {resultSummary}
            </div>
          </div>
        </div>
      </FilterBarContext.Provider>
    );
  }
);

/**
 * The bar's search.
 *
 * Holds the typed text itself so typing is never delayed, and reports it after `debounceMs` of quiet.
 * A value that changes from outside (Back, Clear all, a shared link) replaces the text. Emptying the
 * field, by its clear button or Escape, is reported at once rather than after the wait: nobody is
 * still typing.
 */
export const FilterBarSearch = ({
  value,
  onValueChange,
  debounceMs = 300,
  rootClassName,
  ...rest
}: FilterBarSearchProps) => {
  const bar = useContext(FilterBarContext);
  const [text, setText] = useState(value);
  const wrapper = useRef<HTMLDivElement>(null);
  const reported = useRef(value);

  const debounced = useDebouncedCallback((next: string) => {
    reported.current = next;
    onValueChange(next);
  }, debounceMs);

  // A change we did not report replaces what is typed.
  useEffect(() => {
    if (value !== reported.current) {
      reported.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    bar?.registerSearch({
      reset: () => {
        debounced.cancel();
        reported.current = '';
        setText('');
      },
      focus: () => wrapper.current?.querySelector('input')?.focus(),
    });
    return () => bar?.registerSearch(null);
  }, [bar, debounced]);

  return (
    <div ref={wrapper} className={cn(filterBarSearchRecipe(), rootClassName)} data-part="search">
      <Search
        {...rest}
        value={text}
        onValueChange={(next) => {
          setText(next);
          if (next === '') {
            debounced.cancel();
            reported.current = '';
            onValueChange('');
          } else {
            debounced.call(next.trim());
          }
        }}
      />
    </div>
  );
};
